import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = String(
  process.env.REACT_APP_API_URL || "https://streamsite-v8dd.onrender.com"
)
  .trim()
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      ""
    );
  } catch {
    return "";
  }
}

function formatDate(value) {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function friendlyError(value) {
  const error = String(value || "");
  if (error === "founder_only") return "This inbox is private and only available to the GAStreaming founder account.";
  if (error === "valid_recipient_required") return "Enter a valid sponsor email address.";
  if (error === "subject_and_message_required") return "Add both a subject and a message.";
  if (error === "reply_required") return "Write a reply before sending.";
  if (error === "message_delete_failed") return "That email could not be deleted. Please try again.";
  return error.replaceAll("_", " ") || "Something went wrong.";
}

export default function SponsorInbox() {
  const [data, setData] = useState({ address: "team@gastreaming.live", unread: 0, messages: [], sent: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("inbox");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "" });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");

  const apiFetch = useCallback(async (path, options = {}) => {
    const token = getToken();
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.ok === false) {
      const requestError = new Error(payload?.error || `Request failed (${response.status})`);
      requestError.status = response.status;
      throw requestError;
    }
    return payload;
  }, []);

  const loadInbox = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const payload = await apiFetch("/api/sponsor-inbox");
      setData({
        address: payload.address || "team@gastreaming.live",
        unread: Number(payload.unread || 0),
        messages: Array.isArray(payload.messages) ? payload.messages : [],
        sent: Array.isArray(payload.sent) ? payload.sent : [],
      });
      setSelectedId((current) => current || payload.messages?.find((message) => !message.archivedAt)?.id || "");
    } catch (requestError) {
      setError(friendlyError(requestError?.message));
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  const inboxMessages = useMemo(
    () => data.messages.filter((message) => (showArchived ? Boolean(message.archivedAt) : !message.archivedAt)),
    [data.messages, showArchived]
  );
  const selected = useMemo(
    () => (tab === "sent" ? data.sent : data.messages).find((message) => message.id === selectedId) || null,
    [data.messages, data.sent, selectedId, tab]
  );

  async function updateMessage(messageId, action) {
    setBusy(`${action}:${messageId}`);
    setError("");
    try {
      await apiFetch(`/api/sponsor-inbox/messages/${encodeURIComponent(messageId)}`, {
        method: "PATCH",
        body: JSON.stringify({ action }),
      });
      await loadInbox(true);
      if (action === "archive" || action === "restore") setSelectedId("");
    } catch (requestError) {
      setError(friendlyError(requestError?.message));
    } finally {
      setBusy("");
    }
  }

  async function openMessage(message) {
    setSelectedId(message.id);
    setComposeOpen(false);
    setReply("");
    if (!message.readAt && !message.archivedAt) await updateMessage(message.id, "read");
  }

  async function deleteMessage(message) {
    if (!message?.id) return;
    const folder = tab === "sent" ? "sent" : "messages";
    const label = tab === "sent" ? "sent email" : "email and its saved replies";
    const confirmed = window.confirm(
      `Delete this ${label} permanently from the GAStreaming Sponsor Inbox? This cannot be undone.`
    );
    if (!confirmed) return;

    setBusy(`delete:${message.id}`);
    setError("");
    setNotice("");
    try {
      await apiFetch(`/api/sponsor-inbox/${folder}/${encodeURIComponent(message.id)}`, {
        method: "DELETE",
      });
      setSelectedId("");
      setReply("");
      setNotice("Email deleted from the GAStreaming Sponsor Inbox.");
      await loadInbox(true);
    } catch (requestError) {
      setError(friendlyError(requestError?.message));
    } finally {
      setBusy("");
    }
  }

  async function sendNewEmail(event) {
    event.preventDefault();
    setBusy("compose");
    setError("");
    setNotice("");
    try {
      await apiFetch("/api/sponsor-inbox/send", {
        method: "POST",
        body: JSON.stringify(compose),
      });
      setCompose({ to: "", subject: "", body: "" });
      setComposeOpen(false);
      setTab("sent");
      setSelectedId("");
      setNotice("Sponsor email sent from team@gastreaming.live.");
      await loadInbox(true);
    } catch (requestError) {
      setError(friendlyError(requestError?.message));
    } finally {
      setBusy("");
    }
  }

  async function sendReply(event) {
    event.preventDefault();
    if (!selected) return;
    setBusy(`reply:${selected.id}`);
    setError("");
    setNotice("");
    try {
      await apiFetch(`/api/sponsor-inbox/messages/${encodeURIComponent(selected.id)}/reply`, {
        method: "POST",
        body: JSON.stringify({ body: reply }),
      });
      setReply("");
      setNotice(`Reply sent from ${data.address}.`);
      await loadInbox(true);
    } catch (requestError) {
      setError(friendlyError(requestError?.message));
    } finally {
      setBusy("");
    }
  }

  async function openAttachment(messageId, attachment) {
    setBusy(`attachment:${attachment.id}`);
    setError("");
    try {
      const payload = await apiFetch(
        `/api/sponsor-inbox/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachment.id)}`
      );
      window.open(payload.url, "_blank", "noopener,noreferrer");
    } catch (requestError) {
      setError(friendlyError(requestError?.message));
    } finally {
      setBusy("");
    }
  }

  function switchTab(nextTab) {
    setTab(nextTab);
    setComposeOpen(false);
    setSelectedId("");
    setReply("");
  }

  const currentList = tab === "sent" ? data.sent : inboxMessages;

  return (
    <main className="ga-sponsor-page">
      <style>{`
        .ga-sponsor-page{max-width:1480px;margin:0 auto;padding:18px 18px 48px;color:#fff}
        .ga-sponsor-hero{display:flex;justify-content:space-between;align-items:flex-end;gap:18px;flex-wrap:wrap;margin-bottom:18px;padding:24px;border:1px solid rgba(255,255,255,.13);border-radius:22px;background:radial-gradient(800px 260px at 5% 0%,rgba(220,38,38,.22),transparent 65%),rgba(10,10,13,.88);box-shadow:0 20px 60px rgba(0,0,0,.35)}
        .ga-sponsor-title{margin:0;font-size:clamp(28px,4vw,44px);font-weight:1000;letter-spacing:-1px}.ga-sponsor-sub{margin-top:7px;opacity:.78;line-height:1.5}
        .ga-sponsor-address{color:#ffb3b3;font-weight:900}.ga-sponsor-button{border:1px solid rgba(255,255,255,.16);border-radius:12px;padding:11px 15px;background:#24242a;color:#fff;font-weight:900;cursor:pointer}.ga-sponsor-button:hover{filter:brightness(1.14)}
        .ga-sponsor-button.primary{background:linear-gradient(180deg,#e32636,#9f0712);border-color:#ff5662}.ga-sponsor-button.danger{background:#50151b}.ga-sponsor-button:disabled{opacity:.55;cursor:not-allowed}
        .ga-sponsor-tabs{display:flex;gap:8px;flex-wrap:wrap}.ga-sponsor-tab{border:1px solid rgba(255,255,255,.13);border-radius:999px;padding:9px 14px;background:#17171c;color:#ddd;font-weight:900;cursor:pointer}.ga-sponsor-tab.active{background:#8e0914;color:#fff;border-color:#ed3c49}
        .ga-sponsor-shell{display:grid;grid-template-columns:minmax(300px,390px) minmax(0,1fr);min-height:650px;border:1px solid rgba(255,255,255,.13);border-radius:22px;overflow:hidden;background:rgba(8,8,11,.92);box-shadow:0 20px 70px rgba(0,0,0,.4)}
        .ga-sponsor-list{border-right:1px solid rgba(255,255,255,.11);background:rgba(18,18,22,.92);min-width:0}.ga-sponsor-listbar{padding:14px;border-bottom:1px solid rgba(255,255,255,.1);display:flex;justify-content:space-between;gap:10px;align-items:center}
        .ga-sponsor-item{display:block;width:100%;text-align:left;border:0;border-bottom:1px solid rgba(255,255,255,.08);padding:16px;background:transparent;color:#fff;cursor:pointer}.ga-sponsor-item:hover,.ga-sponsor-item.active{background:rgba(171,15,29,.18)}.ga-sponsor-item.unread{border-left:4px solid #f23343;padding-left:12px}
        .ga-sponsor-itemtop{display:flex;justify-content:space-between;gap:10px}.ga-sponsor-from{font-weight:1000;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ga-sponsor-date{font-size:11px;opacity:.6;white-space:nowrap}.ga-sponsor-subject{margin-top:6px;font-weight:850;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ga-sponsor-preview{margin-top:6px;font-size:13px;opacity:.62;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .ga-sponsor-detail{padding:clamp(18px,3vw,34px);min-width:0}.ga-sponsor-empty{min-height:520px;display:grid;place-items:center;text-align:center;opacity:.68;padding:28px}.ga-sponsor-detailhead{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;flex-wrap:wrap;padding-bottom:18px;border-bottom:1px solid rgba(255,255,255,.1)}
        .ga-sponsor-detail h2{margin:0;font-size:clamp(23px,3vw,34px)}.ga-sponsor-meta{margin-top:8px;opacity:.72;line-height:1.5;word-break:break-word}.ga-sponsor-body{margin-top:22px;padding:20px;border-radius:16px;background:#111116;border:1px solid rgba(255,255,255,.09);white-space:pre-wrap;line-height:1.65;overflow-wrap:anywhere}
        .ga-sponsor-form{display:grid;gap:12px}.ga-sponsor-field{display:grid;gap:7px}.ga-sponsor-field label{font-size:12px;font-weight:1000;letter-spacing:.6px;text-transform:uppercase;opacity:.7}.ga-sponsor-input,.ga-sponsor-textarea{width:100%;box-sizing:border-box;border:1px solid rgba(255,255,255,.16);border-radius:12px;background:#18181e;color:#fff;padding:12px 13px;font:inherit;outline:none}.ga-sponsor-input:focus,.ga-sponsor-textarea:focus{border-color:#f04450;box-shadow:0 0 0 3px rgba(240,68,80,.16)}.ga-sponsor-textarea{min-height:160px;resize:vertical;line-height:1.55}
        .ga-sponsor-replies{margin-top:22px;display:grid;gap:12px}.ga-sponsor-reply{padding:15px;border-radius:14px;background:rgba(129,12,23,.18);border:1px solid rgba(241,73,88,.24)}.ga-sponsor-alert{margin:0 0 14px;padding:12px 14px;border-radius:12px;font-weight:800}.ga-sponsor-alert.error{background:#54131a;color:#ffd3d6;border:1px solid #9f2833}.ga-sponsor-alert.success{background:#103f2e;color:#caffec;border:1px solid #237351}
        .ga-sponsor-attachments{margin-top:15px;display:flex;gap:8px;flex-wrap:wrap}.ga-sponsor-attachment{border:1px solid rgba(255,255,255,.14);border-radius:10px;padding:9px 11px;background:#202027;color:#fff;cursor:pointer}
        @media(max-width:820px){.ga-sponsor-page{padding:10px 10px 35px}.ga-sponsor-shell{grid-template-columns:1fr}.ga-sponsor-list{border-right:0;border-bottom:1px solid rgba(255,255,255,.11);max-height:390px;overflow:auto}.ga-sponsor-detail{padding:18px}.ga-sponsor-hero{padding:18px}}
      `}</style>

      <section className="ga-sponsor-hero">
        <div>
          <div style={{ color: "#ff5965", fontWeight: 1000, letterSpacing: 1.4, fontSize: 12 }}>FOUNDER TOOLS</div>
          <h1 className="ga-sponsor-title">Sponsor Inbox</h1>
          <div className="ga-sponsor-sub">
            Send, receive, and reply as <span className="ga-sponsor-address">{data.address}</span>. Copies of new replies are sent only to your Yahoo address.
          </div>
        </div>
        <button className="ga-sponsor-button primary" onClick={() => { setComposeOpen(true); setSelectedId(""); setNotice(""); }}>
          ＋ New Sponsor Email
        </button>
      </section>

      {error ? <div className="ga-sponsor-alert error">{error}</div> : null}
      {notice ? <div className="ga-sponsor-alert success">{notice}</div> : null}

      <div className="ga-sponsor-tabs" style={{ marginBottom: 12 }}>
        <button className={`ga-sponsor-tab ${tab === "inbox" ? "active" : ""}`} onClick={() => switchTab("inbox")}>
          Inbox {data.unread ? `(${data.unread})` : ""}
        </button>
        <button className={`ga-sponsor-tab ${tab === "sent" ? "active" : ""}`} onClick={() => switchTab("sent")}>
          Sent ({data.sent.length})
        </button>
      </div>

      <section className="ga-sponsor-shell">
        <aside className="ga-sponsor-list">
          <div className="ga-sponsor-listbar">
            <strong>{tab === "sent" ? "Sent mail" : showArchived ? "Archived" : "Incoming mail"}</strong>
            {tab === "inbox" ? (
              <button className="ga-sponsor-button" style={{ padding: "7px 10px", fontSize: 12 }} onClick={() => { setShowArchived((current) => !current); setSelectedId(""); }}>
                {showArchived ? "Show inbox" : "Archived"}
              </button>
            ) : null}
          </div>
          {loading ? (
            <div style={{ padding: 20, opacity: 0.7 }}>Loading sponsor mail…</div>
          ) : currentList.length === 0 ? (
            <div style={{ padding: 20, opacity: 0.62 }}>No {tab === "sent" ? "sent messages" : showArchived ? "archived messages" : "sponsor replies"} yet.</div>
          ) : (
            currentList.map((message) => (
              <button
                key={message.id}
                className={`ga-sponsor-item ${selectedId === message.id ? "active" : ""} ${tab === "inbox" && !message.readAt ? "unread" : ""}`}
                onClick={() => tab === "inbox" ? openMessage(message) : setSelectedId(message.id)}
              >
                <div className="ga-sponsor-itemtop">
                  <span className="ga-sponsor-from">{tab === "sent" ? message.to : message.fromName || message.fromEmail || message.from}</span>
                  <span className="ga-sponsor-date">{formatDate(message.receivedAt || message.createdAt)}</span>
                </div>
                <div className="ga-sponsor-subject">{message.subject || "(No subject)"}</div>
                <div className="ga-sponsor-preview">{message.bodyText || ""}</div>
              </button>
            ))
          )}
        </aside>

        <div className="ga-sponsor-detail">
          {composeOpen ? (
            <form className="ga-sponsor-form" onSubmit={sendNewEmail}>
              <div>
                <div style={{ color: "#ff5965", fontSize: 12, fontWeight: 1000, letterSpacing: 1 }}>NEW OUTREACH</div>
                <h2 style={{ marginTop: 5 }}>Send a sponsor email</h2>
                <div className="ga-sponsor-meta">Sponsors will see {data.address} as the sender and reply address.</div>
              </div>
              <div className="ga-sponsor-field"><label>To</label><input className="ga-sponsor-input" type="email" required value={compose.to} onChange={(event) => setCompose((current) => ({ ...current, to: event.target.value }))} placeholder="partnerships@company.com" /></div>
              <div className="ga-sponsor-field"><label>Subject</label><input className="ga-sponsor-input" required maxLength={300} value={compose.subject} onChange={(event) => setCompose((current) => ({ ...current, subject: event.target.value }))} placeholder="GAStreaming tournament partnership" /></div>
              <div className="ga-sponsor-field"><label>Message</label><textarea className="ga-sponsor-textarea" required maxLength={30000} value={compose.body} onChange={(event) => setCompose((current) => ({ ...current, body: event.target.value }))} placeholder="Write the sponsor outreach here…" /></div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="ga-sponsor-button primary" disabled={busy === "compose"}>{busy === "compose" ? "Sending…" : "Send from team@gastreaming.live"}</button>
                <button type="button" className="ga-sponsor-button" onClick={() => setComposeOpen(false)}>Cancel</button>
              </div>
            </form>
          ) : !selected ? (
            <div className="ga-sponsor-empty">
              <div><div style={{ fontSize: 46 }}>✉️</div><h2 style={{ margin: "8px 0" }}>GAStreaming Sponsor Mail</h2><div>Select a message or start a new sponsor email.</div></div>
            </div>
          ) : (
            <>
              <div className="ga-sponsor-detailhead">
                <div>
                  <h2>{selected.subject || "(No subject)"}</h2>
                  <div className="ga-sponsor-meta">
                    {tab === "sent" ? <>To: <strong>{selected.to}</strong></> : <>From: <strong>{selected.from || selected.fromEmail}</strong></>}
                    <br />{formatDate(selected.receivedAt || selected.createdAt)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {tab === "inbox" ? (
                    <>
                      <button className="ga-sponsor-button" disabled={Boolean(busy)} onClick={() => updateMessage(selected.id, selected.readAt ? "unread" : "read")}>{selected.readAt ? "Mark unread" : "Mark read"}</button>
                      <button className="ga-sponsor-button danger" disabled={Boolean(busy)} onClick={() => updateMessage(selected.id, selected.archivedAt ? "restore" : "archive")}>{selected.archivedAt ? "Restore" : "Archive"}</button>
                    </>
                  ) : null}
                  <button
                    className="ga-sponsor-button danger"
                    disabled={Boolean(busy)}
                    onClick={() => deleteMessage(selected)}
                  >
                    {busy === `delete:${selected.id}` ? "Deleting…" : "Delete email"}
                  </button>
                </div>
              </div>

              <div className="ga-sponsor-body">{selected.bodyText || "(No readable message body)"}</div>

              {tab === "inbox" && selected.attachments?.length ? (
                <div className="ga-sponsor-attachments">
                  {selected.attachments.map((attachment) => (
                    <button key={attachment.id} className="ga-sponsor-attachment" disabled={busy === `attachment:${attachment.id}`} onClick={() => openAttachment(selected.id, attachment)}>
                      📎 {attachment.filename}{busy === `attachment:${attachment.id}` ? "…" : ""}
                    </button>
                  ))}
                </div>
              ) : null}

              {tab === "inbox" && selected.replies?.length ? (
                <div className="ga-sponsor-replies">
                  <h3 style={{ marginBottom: 0 }}>Replies from {data.address}</h3>
                  {selected.replies.map((item) => (
                    <div className="ga-sponsor-reply" key={item.id}>
                      <div style={{ fontSize: 12, opacity: 0.66 }}>{formatDate(item.createdAt)} · To {item.to}</div>
                      <div style={{ marginTop: 8, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{item.bodyText}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              {tab === "inbox" ? (
                <form className="ga-sponsor-form" style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,.1)" }} onSubmit={sendReply}>
                  <div className="ga-sponsor-field"><label>Reply as {data.address}</label><textarea className="ga-sponsor-textarea" required maxLength={30000} value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Write your reply…" /></div>
                  <div><button className="ga-sponsor-button primary" disabled={busy === `reply:${selected.id}`}>{busy === `reply:${selected.id}` ? "Sending…" : "Send reply"}</button></div>
                </form>
              ) : null}
            </>
          )}
        </div>
      </section>
    </main>
  );
}
